import axios from 'axios';

interface ODataPage<T> {
    value: T[];
    '@odata.count'?: number;
}

/**
 * Fetch every row of an OData collection by walking `$skip` / `$top` driven by
 * `@odata.count`. Lifted from `onboarding_details_v2.tsx` so multiple ccn
 * surfaces (the supplier-detail page and the Onboarding Intelligence modal)
 * share a single implementation.
 *
 * The caller's URL may already contain `$filter`, etc. We always force
 * `$count=true` so the response includes the total used to terminate the
 * loop, and add `$top` / `$skip` per-page.
 */
export async function fetchAllODataPages<T>(
    initialUrl: string,
    pageSize = 100
): Promise<T[]> {
    if (!initialUrl) return [];

    const out: T[] = [];
    const [basePath, initialQuery = ''] = initialUrl.split('?');
    const baseParams = new URLSearchParams(initialQuery);
    if (!baseParams.has('$count')) {
        baseParams.set('$count', 'true');
    }

    let skip = 0;
    let total: number | null = null;

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const params = new URLSearchParams(baseParams.toString());
        params.set('$top', String(pageSize));
        params.set('$skip', String(skip));
        const pageUrl = `${basePath}?${params.toString()}`;

        const { data } = await axios.get<ODataPage<T>>(pageUrl);
        const rows = Array.isArray(data?.value) ? data.value : [];
        out.push(...rows);

        skip += rows.length;
        const countValue = Number(data?.['@odata.count']);
        if (!Number.isNaN(countValue)) total = countValue;

        if (rows.length === 0) break;
        if (total !== null && skip >= total) break;
    }

    return out;
}
